import { FC } from 'react';

import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftSupplySettlement } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';

import { SettlementLine } from '../../giftSeller/utils/settlement.utils';
import { AdminHistoryTable } from './AdminHistoryTable';
import { DeliveredEntriesSection } from './DeliveredEntriesSection';
import { HistoryYearCalendar } from './HistoryYearCalendar';

// ─────────────────────────────────────────────
// PendingAndHistoryTab
// ─────────────────────────────────────────────

/** `PendingAndHistoryTab` 컴포넌트의 props */
export interface PendingAndHistoryTabProps {
  /** 정산 예정 = 배송완료 & 정산 확정(Draft)인 배송 기반 정산 라인 */
  pendingLines: SettlementLine[];
  /** 정산 예정 로딩 중 여부 */
  pendingLoading: boolean;
  /** 브랜드별 뷰 여부 (admin 또는 여러 브랜드 담당 셀러). 업체 컬럼·admin 스타일 완료내역 표를 사용 */
  brandView: boolean;
  /** 다음 정산 예정일 정보. 미설정 시 undefined */
  nextSettlement: { year: number; month: number; day: number; dDay: number } | undefined;
  /** 정산 완료 내역 목록 */
  historyItems: GiftSupplySettlement[] | undefined;
  /** 정산 완료 내역 로딩 중 여부 */
  historyLoading: boolean;
  /** 공급사 본인의 정산정보 (비관리자 뷰에서 HistoryYearCalendar에 전달) */
  mySettlementInfo: SettlementInfo | undefined;
  /**
   * 업체 id → `SettlementInfo` 조회 함수 (관리자 뷰 AdminHistoryTable에서 surcharge 분해에 사용)
   */
  settlementByBrand: (brandId?: string) => SettlementInfo | undefined;
}

/**
 * "정산 예정 및 완료 내역" 탭의 전체 컨텐츠를 렌더링하는 컴포넌트.
 *
 * - **정산 예정** 섹션: `DeliveredEntriesSection`으로 미정산 배송건을 표시한다.
 * - **정산 완료 내역** 섹션:
 *   - 브랜드별 뷰(admin·여러 브랜드 셀러) → `AdminHistoryTable` (업체 컬럼·정렬 포함 테이블)
 *   - 단일 브랜드 공급사 뷰 → `HistoryYearCalendar` (연도별 12개월 캘린더)
 */
export const PendingAndHistoryTab: FC<PendingAndHistoryTabProps> = ({
  pendingLines,
  pendingLoading,
  brandView,
  nextSettlement,
  historyItems,
  historyLoading,
  mySettlementInfo,
  settlementByBrand,
}) => {
  return (
    <div>
      {/* ===== 정산 예정 섹션 (배송완료 & 정산 확정 Draft 배송) — 배송완료 탭과 동일 형식 ===== */}
      <h3 className="mb-3 font-semibold text-gray-700">정산 예정</h3>
      <DeliveredEntriesSection
        description="배송완료되어 정산이 확정된 내역입니다. 다음 정산일에 지급됩니다."
        lines={pendingLines}
        loading={pendingLoading}
        brandView={brandView}
        emptyMessage="정산 예정 건이 없습니다."
        amountLabel="총 정산예정금액 (VAT 포함)"
        settlementAmountLabel="= 정산금액 (VAT 포함)"
        nextSettlementInfo={nextSettlement}
        showFilters={false}
        fullItemName
      />

      {/* ===== 정산 완료 내역 섹션 (지급완료 Paid 문서) ===== */}
      <h3 className="mb-3 mt-10 font-semibold text-gray-700">정산 완료 내역</h3>
      {brandView ? (
        <AdminHistoryTable
          historyItems={historyItems}
          loading={historyLoading}
          settlementByBrand={settlementByBrand}
        />
      ) : (
        <HistoryYearCalendar
          historyItems={historyItems}
          loading={historyLoading}
          mySettlementInfo={mySettlementInfo}
        />
      )}
    </div>
  );
};
